import { commitOptions } from "@/lib/api/commitSeams";
import { api } from "@/lib/api/client";

export type ConfigurableMeetingMethod = "zoom" | "google_meet" | "in_person" | "other";

export interface InstitutionSettings {
  tenant: { id: string; slug: string; name: string };
  settings_version: number;
  course_delivery: {
    enabled_meeting_methods: ConfigurableMeetingMethod[];
    default_meeting_method: ConfigurableMeetingMethod;
    locations: string[];
    usage_counts: Record<ConfigurableMeetingMethod, number>;
    managed_meeting_creation: Record<"zoom" | "google_meet", boolean>;
  };
  provider_administration: {
    zoom_recording_receiver: boolean;
  };
  /** Null for a tenant whose recordings are not shared by link. */
  recording_signin: RecordingSignin | null;
  recent_changes: Array<{
    id: string;
    actor_user_id: string;
    category: "course_delivery";
    action: string;
    before_state: Record<string, unknown>;
    after_state: Record<string, unknown>;
    reason: string | null;
    created_at: string;
  }>;
}

export interface RecordingSignin {
  attested: boolean;
  attested_at: string | null;
  attested_by_name: string | null;
  signin_domain: string | null;
}

export interface CourseDeliveryUpdate {
  expected_version: number;
  enabled_meeting_methods: ConfigurableMeetingMethod[];
  default_meeting_method: ConfigurableMeetingMethod;
  locations: string[];
  reason?: string | null;
}

/**
 * The `current` a course delivery save carries when the settings moved since
 * `expected_version`: only the version it met. The settings themselves come
 * from a fresh read.
 */
export interface CourseDeliveryConflict {
  settings_version: number;
}

export interface ZoomRecordingReceiverStatus {
  capability_id: "zoom.recordings";
  current_actor_user_id: string;
  provider_configured: boolean;
  ingestion_enabled: boolean;
  ready: boolean;
  receiver: {
    user_id: string;
    enabled: boolean;
    authorized: boolean;
    verified_at: string;
    updated_at: string;
  } | null;
}

export interface SettingsRanking {
  state: "ready" | "empty" | "unavailable";
  value?: { candidates: Array<{ id: string; rank: number | null; eligible: boolean; blocked: boolean }>; lead: string | null };
}
export const settingsApi = {
  ranking: () => api.get<SettingsRanking>("/admin/settings/ranking"),
  get: () => api.get<InstitutionSettings>("/admin/settings"),
  attestRecordingSignin: (attested: boolean, key: string) =>
    api.post<{ recording_signin: RecordingSignin | null; receipt: string }>(
      "/admin/settings/recordings/signin-attestation",
      { attested }, commitOptions(key),
    ),
  updateCourseDelivery: (body: CourseDeliveryUpdate, key: string) => api.patch<InstitutionSettings & { committed_version?: number }>("/admin/settings/course-delivery", body, commitOptions(key)),
};

export const zoomRecordingReceiverApi = {
  get: () => api.get<ZoomRecordingReceiverStatus>("/admin/zoom/recordings/receiver"),
  bindCurrent: (key: string) =>
    api.post<ZoomRecordingReceiverStatus>("/admin/zoom/recordings/receiver/self", undefined, commitOptions(key)),
};
